import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, FormSection, PageHeader, cx, ThemeSwitch } from '@timely/ui';
import { useSession } from '../auth/SessionProvider';
import { supabase } from '../lib/supabaseClient';
import { useActiveWorkspace, useWorkspaces } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { Field, FormNotice, PasswordRules } from './AuthLayout';
import { passwordProblems } from '../auth/authErrors';

/**
 * Your account (Collection OS Phase 0; docs/product/phase-0/03-PROFILE-AND-ACCOUNT-SECURITY.md):
 * the things that are yours rather than the company's — your name, your
 * password, your signed-in devices — and the companies you belong to.
 *
 * Everything goes through Supabase Auth or one database function, as the
 * person signed in: nobody sets a password for somebody else, no password is
 * stored or shown anywhere but the field it is typed in, and the only row a
 * person can change is their own name (`set_my_display_name`, 0037).
 */

const NAME_MAX = 80;
const NETWORK = 'We could not reach the server. Check your connection and try again.';
const RATE_LIMITED = 'Too many attempts. Wait a few minutes and try again.';

type Notice = { tone: 'ok' | 'error'; text: string } | null;

export function Account() {
    const { workspace } = useActiveWorkspace();
    const { contexts } = useWorkspaces();
    const { email } = useSession();
    const companies = contexts.filter(c => c.membership.status === 'active');

    return (
        <Page width="standard">
            <PageHeader title="Your account" subtitle="Your name, your password and where you are signed in. These are yours, in every company you belong to." />
            {/* One form in groups, each divided from the next by a hairline: the group's name and purpose on the left,
                its fields on the right (FormSection), rather than seven cards stacked down the page. */}
            <div>
                <YourName />
                <FormSection title="Appearance" description="Light, dark, or the same as this device. Kept in this browser, for you alone.">
                    <ThemeSwitch />
                </FormSection>
                <FormSection title="Email" description="The address you sign in with.">
                    <p className="text-body font-semibold text-label break-all">{email ?? '—'}</p>
                    <p className="mt-1 text-secondary text-label-3">
                        Changing it is not offered here yet: your invitations and your companies’ records are tied to it. Ask Timely support if it must change.
                    </p>
                </FormSection>
                <FormSection title="Your companies" description="Where you work in Timely, and your role in each. An owner or admin changes roles in Team.">
                    <ul className="divide-y divide-separator -mt-2">
                        {companies.map(c => (
                            <li key={c.workspace.id} className="py-2.5 flex items-center justify-between gap-3 flex-wrap">
                                <span className="text-body text-label font-semibold break-words min-w-0">
                                    {c.workspace.name}
                                    {c.workspace.id === workspace.id && <span className="ml-2 text-caption text-label-3 font-normal">(this company)</span>}
                                </span>
                                <span className="text-secondary text-label-2">{c.membership.roleName}</span>
                            </li>
                        ))}
                    </ul>
                </FormSection>
                <ChangePassword />
                <SignedInDevices />
                <FormSection title="Two-step verification" description="A code from an authenticator app, as well as your password.">
                    <p className="text-body text-label-2">
                        Not available yet. Until it is, use a password you use nowhere else, and sign out of devices you no longer use.
                    </p>
                </FormSection>
            </div>
        </Page>
    );
}

/** Your name, as your team sees it, in every company you belong to. */
function YourName() {
    const { membership } = useActiveWorkspace();
    const { reload } = useWorkspaces();
    const [name, setName] = useState(membership.displayName);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState<Notice>(null);
    const trimmed = name.replace(/\s+/g, ' ').trim();
    const problem = trimmed.length === 0 ? 'Enter your name.' : trimmed.length > NAME_MAX ? `Use at most ${NAME_MAX} characters.` : null;

    const save = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy || problem) return;
        setBusy(true);
        setNotice(null);
        const { data, error } = await supabase().rpc('set_my_display_name', { p_name: trimmed });
        if (error) {
            setBusy(false);
            setNotice({
                tone: 'error',
                text: error.code === '53300' ? 'You have changed your name many times in the last hour. Try again later.'
                    : error.code === '23514' ? `Use 1 to ${NAME_MAX} characters of ordinary text.`
                        : /fetch|network/i.test(error.message) ? NETWORK : 'Your name could not be saved just now. Try again in a moment.',
            });
            return;
        }
        // So a company you join later knows you by it too. Not essential: the companies you are in already do.
        await supabase().auth.updateUser({ data: { name: trimmed } }).catch(() => undefined);
        await reload();
        setBusy(false);
        const changed = typeof data === 'number' ? data : 0;
        setNotice({ tone: 'ok', text: changed === 0 ? 'That is already your name.' : `Saved. Your team now sees “${trimmed}”.` });
    };

    return (
        <FormSection title="Your name" description="How your team sees you: on accounts you are given, in history, and in the audit log.">
            <form onSubmit={save} noValidate className="max-w-md">
                {notice && <FormNotice tone={notice.tone}>{notice.text}</FormNotice>}
                <Field
                    label="Name"
                    name="display-name"
                    autoComplete="name"
                    value={name}
                    maxLength={NAME_MAX + 20}
                    onChange={e => { setName(e.target.value); setNotice(null); }}
                    error={name !== membership.displayName && problem ? problem : null}
                    hint="Shown in every company you belong to."
                />
                <Button type="submit" variant="primary" busy={busy} disabled={Boolean(problem) || trimmed === membership.displayName}>
                    {busy ? 'Saving…' : 'Save name'}
                </Button>
            </form>
        </FormSection>
    );
}

/**
 * A new password, proven by the current one — to Supabase Auth, not to this
 * page (Phase 0 hardening; docs/product/phase-0/03 §2). The current password
 * travels with the new one in a single `updateUser` call, and Auth refuses the
 * change unless it is right (`update_password_require_current_password`); a
 * direct call to Auth without it is refused the same way, so nothing here is
 * the security boundary. Auth also applies the sign-up rules and ends every
 * other session; the explicit sign-out below makes sure. Nothing typed is kept
 * anywhere but these fields, and they are emptied as soon as they are used.
 */
function ChangePassword() {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [again, setAgain] = useState('');
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState<Notice>(null);
    const problems = passwordProblems(next);
    const mismatch = again.length > 0 && again !== next ? 'The two new passwords are different.' : null;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setNotice(null);
        // What the form can say before asking: Auth checks all of it again, and the current password only Auth can check.
        if (!current) { setNotice({ tone: 'error', text: 'Enter your current password.' }); return; }
        if (problems.length > 0) { setNotice({ tone: 'error', text: 'The new password does not meet the rules below.' }); return; }
        if (next !== again) { setNotice({ tone: 'error', text: 'The two new passwords are different.' }); return; }
        if (next === current) { setNotice({ tone: 'error', text: 'Choose a password different from your current one.' }); return; }

        setBusy(true);
        const client = supabase();
        const { error } = await client.auth.updateUser({ password: next, current_password: current });
        if (error) {
            setBusy(false);
            setCurrent('');
            setNotice({ tone: 'error', text: passwordChangeError(error) });
            return;
        }
        // Auth has already ended every other session; this makes sure, whatever its version does.
        await client.auth.signOut({ scope: 'others' }).catch(() => undefined);
        setBusy(false);
        setCurrent(''); setNext(''); setAgain('');
        setNotice({ tone: 'ok', text: 'Password changed. Every other browser and phone signed in as you is signed out within the hour; this one stays signed in.' });
    };

    return (
        <FormSection title="Password" description="At least 10 characters, with upper- and lower-case letters and a digit.">
            <form onSubmit={submit} noValidate className="max-w-md">
                {notice && <FormNotice tone={notice.tone}>{notice.text}</FormNotice>}
                <Field label="Current password" name="current-password" type="password" autoComplete="current-password"
                    value={current} onChange={e => setCurrent(e.target.value)} required
                    hint="Checked by Timely's sign-in service before anything changes." />
                <Field label="New password" name="new-password" type="password" autoComplete="new-password"
                    value={next} onChange={e => setNext(e.target.value)} required hint={<PasswordRules problems={problems} />} />
                <Field label="New password, again" name="new-password-again" type="password" autoComplete="new-password"
                    value={again} onChange={e => setAgain(e.target.value)} required error={mismatch} />
                <p className="mb-4 text-secondary text-label-3">Changing your password signs you out on every other device, within the hour.</p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <Button type="submit" variant="primary" busy={busy}>{busy ? 'Changing…' : 'Change password'}</Button>
                    <Link to="/forgot-password" className="text-secondary font-semibold text-accent-text hover:underline">Forgot your current password?</Link>
                </div>
            </form>
        </FormSection>
    );
}

/**
 * Auth's refusal, in words that name the problem and nothing else: never a
 * password, never which of the two passwords Auth looked at, never its own text.
 */
export function passwordChangeError(error: { code?: string; message?: string; status?: number; reasons?: string[] } | null): string {
    const code = error?.code ?? '';
    const text = `${code} ${error?.message ?? ''}`.toLowerCase();
    if (/^current_password/.test(code) || /current password/.test(text)) return 'Your current password is not right. Nothing was changed.';
    if (code === 'over_request_rate_limit' || error?.status === 429 || /rate limit|too many/.test(text)) return RATE_LIMITED;
    if (code === 'same_password' || /different from the old/.test(text)) return 'Choose a password different from your current one.';
    if (code === 'weak_password' && (error?.reasons ?? []).includes('pwned')) return 'That password has appeared in a data breach elsewhere. Choose a different one.';
    if (code === 'weak_password' || /weak|password should/.test(text)) return 'The new password does not meet the rules below.';
    if (code === 'reauthentication_needed' || code === 'session_not_found' || error?.status === 401) return 'Sign in again, then change your password.';
    if (/fetch|network/.test(text)) return NETWORK;
    return 'Your password could not be changed just now. Nothing was changed.';
}

/**
 * Signing out everywhere else. Supabase ends every other session's refresh;
 * a device already signed in keeps working only until its current access runs
 * out, within an hour. Timely cannot list your devices yet, and does not
 * pretend to.
 */
function SignedInDevices() {
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState<Notice>(null);
    const run = async () => {
        setBusy(true);
        setNotice(null);
        const { error } = await supabase().auth.signOut({ scope: 'others' });
        setBusy(false);
        setNotice(error
            ? { tone: 'error', text: /fetch|network/i.test(error.message) ? NETWORK : 'That could not be done just now. Try again in a moment.' }
            : { tone: 'ok', text: 'Done. Every other browser and phone signed in as you is signed out within the hour; this one stays signed in.' });
    };
    return (
        <FormSection title="Signed-in devices" description="Lost a phone, or signed in on a shared computer? End those sessions from here.">
            {notice && <FormNotice tone={notice.tone}>{notice.text}</FormNotice>}
            <div className={cx('flex flex-wrap items-center gap-3')}>
                <Button variant="secondary" busy={busy} onClick={() => { void run(); }}>Sign out everywhere else</Button>
                <span className="text-secondary text-label-3">This device stays signed in.</span>
            </div>
        </FormSection>
    );
}
